import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Save, Loader2, CheckCircle2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

// Registry of all configurable pages — grouped for easy navigation
const PAGE_GROUPS = [
    {
        label: "Simple Pages",
        pages: [
            { id: "blog", label: "Blog Listing", url: "/blog", sections: ["hero"] },
            { id: "success-stories", label: "Success Stories", url: "/success-stories", sections: ["hero"] },
            { id: "engagement-models", label: "Engagement Models", url: "/engagement-models", sections: ["hero"] },
            { id: "schedule-meeting", label: "Schedule Meeting", url: "/schedule-meeting", sections: ["hero", "meeting"] },
            { id: "request-quote", label: "Request Quote", url: "/request-quote", sections: ["hero"] },
        ],
    },
    {
        label: "What We Do",
        pages: [
            { id: "what-we-do", label: "Services Index", url: "/what-we-do", sections: ["hero", "footer"] },
        ],
    },
    {
        label: "Services",
        pages: [
            { id: "service-product-engineering", label: "Product Engineering", url: "/what-we-do/product-engineering", sections: ["hero", "footer"] },
            { id: "service-intelligent-automation", label: "Agentic AI", url: "/what-we-do/intelligent-automation", sections: ["hero", "footer"] },
            { id: "service-data-ai", label: "Data & AI", url: "/what-we-do/data-ai", sections: ["hero", "footer"] },
            { id: "service-analytics", label: "Analytics", url: "/what-we-do/analytics", sections: ["hero", "footer"] },
            { id: "service-cloud-devops", label: "Cloud & DevOps", url: "/what-we-do/cloud-devops", sections: ["hero", "footer"] },
            { id: "service-testing", label: "Quality Engineering", url: "/what-we-do/testing", sections: ["hero", "footer"] },
            { id: "service-security", label: "Security", url: "/what-we-do/security", sections: ["hero", "footer"] },
        ],
    },
    {
        label: "Industries",
        pages: [
            { id: "industry-retail", label: "Retail", url: "/solutions/retail", sections: ["footer"] },
            { id: "industry-bfsi", label: "BFSI", url: "/solutions/bfsi", sections: ["footer"] },
            { id: "industry-logistics", label: "Logistics", url: "/solutions/logistics", sections: ["footer"] },
            { id: "industry-food-and-beverage", label: "Food & Beverage", url: "/solutions/food-and-beverage", sections: ["footer"] },
            { id: "industry-hospitality", label: "Hospitality", url: "/solutions/hospitality", sections: ["footer"] },
            { id: "industry-energy-and-utilities", label: "Energy & Utilities", url: "/solutions/energy-and-utilities", sections: ["footer"] },
            { id: "industry-manufacturing", label: "Manufacturing", url: "/solutions/manufacturing", sections: ["footer"] },
        ],
    },
];

const HERO_FIELDS = [
    { key: "hero_headline", label: "Headline", type: "text", placeholder: "Page main headline" },
    { key: "hero_subheadline", label: "Sub-headline", type: "textarea", rows: 3, placeholder: "Supporting paragraph below the headline" },
    { key: "hero_primary_label", label: "Primary Button Label", type: "text", placeholder: "e.g. Schedule a Tech Consultation" },
    { key: "hero_primary_url", label: "Primary Button URL", type: "text", placeholder: "e.g. /schedule-meeting" },
    { key: "hero_primary_sub", label: "Primary Button Micro-copy", type: "text", placeholder: "Small text below the button" },
    { key: "hero_secondary_label", label: "Secondary Button Label", type: "text", placeholder: "e.g. Request Quote" },
    { key: "hero_secondary_url", label: "Secondary Button URL", type: "text", placeholder: "e.g. /request-quote" },
    { key: "hero_secondary_sub", label: "Secondary Button Micro-copy", type: "text", placeholder: "Small text below the button" },
];

const SCHEDULE_TAB_FIELDS = [
    { key: "tab1_label", label: "Tab 1 Label", type: "text", placeholder: "Enterprise Engineering / Architecture Audit" },
    { key: "tab2_label", label: "Tab 2 Label", type: "text", placeholder: "InvoiceOS Product Demo & AP Automation" },
];

const SCHEDULE_STATE1_FIELDS = [
    { key: "s1_person_title",          label: "Who You Are Meeting — Title",    type: "text",     placeholder: "Principal Systems Architect & Delivery Lead" },
    { key: "s1_person_sub",            label: "Who You Are Meeting — Sub",      type: "text",     placeholder: "10+ Years Enterprise Production Tenure" },
    { key: "s1_session_label",         label: "Session Section Label",          type: "text",     placeholder: "WHAT TO EXPECT IN YOUR 30-MINUTE SESSION" },
    { key: "s1_agenda_1",              label: "Agenda Item 1",                  type: "text",     placeholder: "[03m] Context & Session Alignment" },
    { key: "s1_agenda_2",              label: "Agenda Item 2",                  type: "text",     placeholder: "[07m] Deep Dive: Architecture & Challenges" },
    { key: "s1_agenda_3",              label: "Agenda Item 3",                  type: "text",     placeholder: "[10m] Proven Engineering Patterns & HLD Case Studies" },
    { key: "s1_agenda_4",              label: "Agenda Item 4",                  type: "text",     placeholder: "[10m] Actionable Next Steps & Delivery Fit" },
    { key: "s1_trust",                 label: "Trust Guarantee",                type: "text",     placeholder: "100% NDA & 24-Hr HLD Proposal SLA" },
    { key: "s1_impact",                label: "Impact Proof",                   type: "text",     placeholder: "Enterprise Cloud & AI Modernization" },
    { key: "s1_button_text",           label: "Submit Button Text",             type: "text",     placeholder: "Confirm 30-Min Architecture Session" },
    { key: "s1_textarea_placeholder",  label: "Step 5 Textarea Placeholder",   type: "textarea", rows: 2, placeholder: "A few sentences about your current system architecture, technical bottlenecks, or target engineering outcomes..." },
];

const SCHEDULE_STATE2_FIELDS = [
    { key: "s2_person_title",          label: "Who You Are Meeting — Title",    type: "text",     placeholder: "Lead InvoiceOS Automation Solutions Architect" },
    { key: "s2_person_sub",            label: "Who You Are Meeting — Sub",      type: "text",     placeholder: "Specialist in Accounts Payable AI" },
    { key: "s2_session_label",         label: "Session Section Label",          type: "text",     placeholder: "WHAT TO EXPECT IN YOUR 30-MINUTE DEMO" },
    { key: "s2_agenda_1",              label: "Agenda Item 1",                  type: "text",     placeholder: "[03m] AP Workflow & ERP Ecosystem Review" },
    { key: "s2_agenda_2",              label: "Agenda Item 2",                  type: "text",     placeholder: "[07m] Live Platform Demo: AI Extraction & Rules" },
    { key: "s2_agenda_3",              label: "Agenda Item 3",                  type: "text",     placeholder: "[10m] Custom Exception Handling & ERP Integration" },
    { key: "s2_agenda_4",              label: "Agenda Item 4",                  type: "text",     placeholder: "[10m] ROI Calculation, Pilot Plan & Pricing" },
    { key: "s2_trust",                 label: "Trust Guarantee",                type: "text",     placeholder: "Free 14-Day Sandbox / PoC Access" },
    { key: "s2_impact",                label: "Impact Proof",                   type: "text",     placeholder: "99.2% Accuracy, 80% Cost Reduction" },
    { key: "s2_button_text",           label: "Submit Button Text",             type: "text",     placeholder: "Request InvoiceOS Product Demo" },
    { key: "s2_textarea_placeholder",  label: "Step 5 Textarea Placeholder",   type: "textarea", rows: 2, placeholder: "Describe your AP workflow challenges, ERP system, and current invoice volume..." },
];

const FOOTER_FIELDS = [
    { key: "footer_headline", label: "Headline", type: "text", placeholder: "CTA section headline" },
    { key: "footer_body", label: "Body Paragraph", type: "textarea", rows: 3, placeholder: "Supporting description" },
    { key: "footer_primary_label", label: "Primary Button Label", type: "text", placeholder: "e.g. Schedule a 30-Min Session" },
    { key: "footer_primary_url", label: "Primary Button URL", type: "text", placeholder: "e.g. /schedule-meeting" },
    { key: "footer_primary_sub", label: "Primary Button Micro-copy", type: "text", placeholder: "Small text below the button" },
    { key: "footer_secondary_label", label: "Secondary Button Label", type: "text", placeholder: "e.g. Get a Fixed-Price Quote" },
    { key: "footer_secondary_url", label: "Secondary Button URL", type: "text", placeholder: "e.g. /request-quote" },
    { key: "footer_secondary_sub", label: "Secondary Button Micro-copy", type: "text", placeholder: "Small text below the button" },
    { key: "footer_anchor_label", label: "Anchor Link Label", type: "text", placeholder: "e.g. Explore InvoiceOS →" },
    { key: "footer_anchor_url", label: "Anchor Link URL", type: "text", placeholder: "e.g. /products/prod-invoiceos" },
];

const inputCls = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-muted-foreground";

function FieldGroup({ title, fields, form, onChange, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="rounded-xl border border-border overflow-hidden">
            <button type="button" onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between px-5 py-4 bg-secondary/40 hover:bg-secondary/70 transition-colors text-left">
                <span className="font-heading text-sm font-semibold">{title}</span>
                {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            </button>
            {open && (
                <div className="p-5 grid gap-4 bg-card">
                    {fields.map(f => (
                        <div key={f.key}>
                            <label className={labelCls}>{f.label}</label>
                            {f.type === "textarea" ? (
                                <textarea rows={f.rows || 3} value={form[f.key] || ""} onChange={e => onChange(f.key, e.target.value)}
                                    placeholder={f.placeholder} className={`mt-1.5 ${inputCls}`} />
                            ) : (
                                <input type="text" value={form[f.key] || ""} onChange={e => onChange(f.key, e.target.value)}
                                    placeholder={f.placeholder} className={`mt-1.5 ${inputCls}`} />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AdminPageContent() {
    const [selectedId, setSelectedId] = useState("blog");
    const [allData, setAllData] = useState({});
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    // Load all page content on mount
    useEffect(() => {
        api.get("/admin/page-content").then(({ data }) => {
            const map = {};
            (data.items || []).forEach(d => { map[d.page_id] = d; });
            setAllData(map);
        }).catch(() => {});
    }, []);

    // When selection changes, populate form
    useEffect(() => {
        setForm(allData[selectedId] || { page_id: selectedId });
        setSaved(false);
    }, [selectedId, allData]);

    const selectedPage = PAGE_GROUPS.flatMap(g => g.pages).find(p => p.id === selectedId);
    const onChange = (key, val) => setForm(f => ({ ...f, [key]: val }));

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(`/admin/page-content/${selectedId}`, form);
            setAllData(prev => ({ ...prev, [selectedId]: data }));
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (e) {
            alert("Save failed: " + (e?.response?.data?.detail || e.message));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-8" data-testid="page-admin-page-content">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Page Banners</h1>
                    <p className="text-muted-foreground mt-1 text-sm">Edit hero banners and footer CTA sections for every page on the website.</p>
                </div>
                {selectedPage && (
                    <a href={selectedPage.url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                        Preview page <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                )}
            </div>

            <div className="grid lg:grid-cols-[260px,1fr] gap-6">
                {/* Page list */}
                <aside className="rounded-xl border border-border bg-white overflow-hidden self-start">
                    {PAGE_GROUPS.map(group => (
                        <div key={group.label}>
                            <div className="px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground bg-secondary/40 border-b border-border">
                                {group.label}
                            </div>
                            {group.pages.map(p => {
                                const configured = !!allData[p.id]?.hero_headline || !!allData[p.id]?.footer_headline || !!allData[p.id]?.hero_primary_label;
                                return (
                                    <button key={p.id} type="button" onClick={() => setSelectedId(p.id)}
                                        data-testid={`page-banner-nav-${p.id}`}
                                        className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left border-b border-border transition-colors last:border-b-0 ${selectedId === p.id ? "bg-primary/10 text-primary font-semibold" : "text-foreground/70 hover:bg-secondary/40"}`}>
                                        <span>{p.label}</span>
                                        {configured && <span className="h-2 w-2 rounded-full bg-green-500 flex-shrink-0" title="Configured" />}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </aside>

                {/* Edit form */}
                <div className="space-y-4">
                    {selectedPage && (
                        <>
                            <div className="flex items-center gap-3 mb-2">
                                <h2 className="font-heading text-xl font-bold">{selectedPage.label}</h2>
                                <code className="text-xs bg-secondary px-2 py-0.5 rounded text-muted-foreground">{selectedPage.url}</code>
                            </div>

                            {selectedPage.sections.includes("hero") && (
                                <FieldGroup title="Hero Banner" fields={HERO_FIELDS} form={form} onChange={onChange} />
                            )}
                            {selectedPage.sections.includes("meeting") && (
                                <>
                                    <FieldGroup title="Tab Labels (Switcher)" fields={SCHEDULE_TAB_FIELDS} form={form} onChange={onChange} />
                                    <FieldGroup title="State 1 — Enterprise Architecture (Right Panel)" fields={SCHEDULE_STATE1_FIELDS} form={form} onChange={onChange} />
                                    <FieldGroup title="State 2 — InvoiceOS Product Demo (Right Panel)" fields={SCHEDULE_STATE2_FIELDS} form={form} onChange={onChange} defaultOpen={false} />
                                </>
                            )}
                            {selectedPage.sections.includes("footer") && (
                                <FieldGroup title="Footer CTA Block" fields={FOOTER_FIELDS} form={form} onChange={onChange} />
                            )}

                            <div className="flex items-center gap-3 pt-2">
                                <button type="button" onClick={save} disabled={saving}
                                    data-testid="page-banner-save"
                                    className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 font-semibold text-white hover:bg-primary/90 transition-colors disabled:opacity-50">
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                    {saving ? "Saving..." : "Save Changes"}
                                </button>
                                {saved && (
                                    <span className="inline-flex items-center gap-1.5 text-sm text-green-600 font-medium" data-testid="page-banner-saved">
                                        <CheckCircle2 className="h-4 w-4" /> Saved
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
